/**
 * Bridges a partner signup code across page/redirect boundaries the React
 * state on the register page can't survive: a full-page Google OAuth
 * redirect, and the gap between registering (no company yet, for Google
 * signups and business/accountant signups without a RUC yet) and later
 * completing onboarding, where the company actually gets created.
 *
 * sessionStorage (not localStorage) deliberately — it should not outlive
 * the tab, and should not follow the user into an unrelated later session.
 *
 * Only ever read by the genuine self-onboarding company-creation flow
 * (onboarding-page.tsx's NoTenantOnboardingView) — NOT by
 * accountant-new-client-page.tsx, which reuses the same POST
 * /auth/create-company endpoint to add client companies to a portfolio.
 * A leftover code must never attach to a client an accountant is adding.
 */
const STORAGE_KEY = "fiscalo:pendingPartnerCode";

export function setPendingPartnerCode(code: string): void {
  try {
    if (code.trim()) {
      window.sessionStorage.setItem(STORAGE_KEY, code.trim());
    } else {
      window.sessionStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    // Private-browsing / storage-disabled — the code just won't survive
    // a redirect. Non-fatal: the same-page register() path still works.
  }
}

export function getPendingPartnerCode(): string {
  try {
    return window.sessionStorage.getItem(STORAGE_KEY)?.trim() ?? "";
  } catch {
    return "";
  }
}

export function clearPendingPartnerCode(): void {
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // no-op
  }
}
